import { FC, useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useVideoContext } from './Video';
import { CaptionSettings } from '@/types/project';
import {
  getEffectiveCaptionSettings,
  getCaptionFontFamily,
  getCaptionFontWeight,
} from '@/components/CaptionSettingsPanel';
import type { FontOption } from '@/components/CaptionSettingsPanel';
import styles from './VideoPlayer.module.scss';

// Reference width for scaling (standard 1080p video width)
// Caption sizes are designed to look correct at this reference width
const REFERENCE_VIDEO_WIDTH = 1920;
const BASE_CAPTION_FONT_SIZE = 48; // Base font size at reference width
const BASE_CAPTION_PADDING_V = 12; // Vertical padding at reference width
const BASE_CAPTION_PADDING_H = 24; // Horizontal padding at reference width
const BASE_CAPTION_BOTTOM = 80; // Bottom offset at reference width
const BASE_HIGHLIGHT_SHADOW = 6; // Highlight box-shadow extension at reference width
const BASE_BORDER_RADIUS = 8; // Border radius at reference width

interface WordItem {
  start: number;
  end: number;
  text: string;
}

interface VideoPlayerProps {
  videoSize?: string;
  headingTitle?: string;
  headingStyle?: any;
  headingContainerStyle?: any;
  logo?: string;
  imageAtr?: {
    image?: Blob;
    position?: any;
  };
  addLogo?: boolean;
  addCaptions?: boolean;
  captionSettings?: CaptionSettings;
  transcriptData?: {
    items: WordItem[];
  } | null;
  /** The start time of the segment in the original video (in seconds). Used to offset caption timing. */
  segmentStartTime?: number;
}

const VideoPlayer: FC<VideoPlayerProps> = ({
  // videoSize,
  headingTitle,
  headingStyle,
  headingContainerStyle,
  logo,
  imageAtr,
  addLogo,
  addCaptions,
  captionSettings,
  transcriptData,
  segmentStartTime = 0,
}) => {
  const context = useVideoContext();

  const { videoRef, videoControls, videoUrl } = context;
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [captionScale, setCaptionScale] = useState(1);
  const [adaptiveBorderColor, setAdaptiveBorderColor] = useState('#000000');

  // Handle video time updates for captions
  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const handleTimeUpdate = (): void => {
      setCurrentTime(video.currentTime);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [videoRef]);

  // Track video container size and calculate caption scale factor
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let rafId: number | null = null;

    const updateScale = (): void => {
      // Cancel any pending animation frame to debounce rapid resize events
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        const containerWidth = container.offsetWidth;
        const scale = containerWidth / REFERENCE_VIDEO_WIDTH;
        setCaptionScale(scale);
        rafId = null;
      });
    };

    // Initial calculation
    updateScale();

    // Use ResizeObserver to track size changes
    const resizeObserver = new ResizeObserver(updateScale);
    resizeObserver.observe(container);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
    };
  }, []);

  // Get effective caption settings
  const effectiveCaptionSettings = useMemo(() => {
    if (!captionSettings) return null;
    return getEffectiveCaptionSettings(captionSettings);
  }, [captionSettings]);

  // Analyze video brightness in the bottom 25% for adaptive contrast
  useEffect(() => {
    if (!effectiveCaptionSettings?.adaptiveContrast) return;

    const video = videoRef?.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    let animationId: number | null = null;

    const analyzeFrame = (): void => {
      if (video.paused || video.ended) {
        animationId = requestAnimationFrame(analyzeFrame);
        return;
      }

      // Set canvas size to a small sample size for performance
      const sampleWidth = 160;
      const sampleHeight = 90;
      canvas.width = sampleWidth;
      canvas.height = sampleHeight;

      // Draw the current video frame
      ctx.drawImage(video, 0, 0, sampleWidth, sampleHeight);

      // Get the bottom 25% of the video
      const bottomStartY = Math.floor(sampleHeight * 0.75);
      const bottomHeight = sampleHeight - bottomStartY;

      // Get pixel data from the bottom 25%
      let imageData;
      try {
        imageData = ctx.getImageData(0, bottomStartY, sampleWidth, bottomHeight);
      } catch (e) {
        // Canvas tainted by cross-origin data - skip analysis
        animationId = requestAnimationFrame(analyzeFrame);
        return;
      }
      const pixels = imageData.data;

      // Calculate average brightness (using luminance formula)
      let totalBrightness = 0;
      const pixelCount = pixels.length / 4;

      for (let i = 0; i < pixels.length; i += 4) {
        const r = pixels[i];
        const g = pixels[i + 1];
        const b = pixels[i + 2];
        // Use relative luminance formula
        const brightness = 0.299 * r + 0.587 * g + 0.114 * b;
        totalBrightness += brightness;
      }

      const avgBrightness = totalBrightness / pixelCount;

      // Threshold: 128 is mid-point (0-255 range)
      // If dark (brightness < 128), use white border; otherwise, use black border
      const newBorderColor = avgBrightness < 128 ? '#FFFFFF' : '#000000';
      setAdaptiveBorderColor(newBorderColor);

      animationId = requestAnimationFrame(analyzeFrame);
    };

    // Start analyzing when video is playing
    const handlePlay = (): void => {
      analyzeFrame();
    };

    const handlePause = (): void => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handlePause);

    // Start analysis if video is already playing
    if (!video.paused) {
      analyzeFrame();
    }

    return () => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
      }
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handlePause);
    };
  }, [effectiveCaptionSettings?.adaptiveContrast, videoRef]);

  // Group words into caption segments
  const captionSegments = useMemo(() => {
    if (!transcriptData?.items) return [];
    const segments: { start: number; end: number; words: WordItem[] }[] = [];
    let currentSegment: { start: number; end: number; words: WordItem[] } | null = null;

    transcriptData.items.forEach((word) => {
      if (!currentSegment) {
        currentSegment = { start: word.start, end: word.end, words: [word] };
      } else {
        const timeSinceStart = word.end - currentSegment.start;
        const gapFromPrevious = word.start - currentSegment.end;

        if (timeSinceStart > 3 || gapFromPrevious > 1) {
          segments.push(currentSegment);
          currentSegment = { start: word.start, end: word.end, words: [word] };
        } else {
          currentSegment.end = word.end;
          currentSegment.words.push(word);
        }
      }
    });

    if (currentSegment) {
      segments.push(currentSegment);
    }

    return segments;
  }, [transcriptData]);

  // Get current caption segment
  // When viewing a segment, we need to offset the video time by the segment's start time
  // because the transcript timings are based on the original video, not the segment
  const getCurrentSegment = useCallback(
    (time: number) => {
      const adjustedTime = time + segmentStartTime;
      return (
        captionSegments.find((seg) => adjustedTime >= seg.start && adjustedTime <= seg.end + 0.5) ||
        null
      );
    },
    [captionSegments, segmentStartTime],
  );

  // Get current highlighted word
  // When viewing a segment, we need to offset the video time by the segment's start time
  const getCurrentHighlightedWord = useCallback(
    (time: number): WordItem | null => {
      if (!transcriptData?.items) return null;
      const adjustedTime = time + segmentStartTime;
      return (
        transcriptData.items.find(
          (item) => adjustedTime >= item.start && adjustedTime <= item.end,
        ) || null
      );
    },
    [transcriptData, segmentStartTime],
  );

  // Render caption text with word-level highlighting
  const renderCaptionText = useCallback(() => {
    if (!effectiveCaptionSettings) return null;

    const segment = getCurrentSegment(currentTime);
    if (!segment) return null;

    const highlightedWord = effectiveCaptionSettings.highlightEnabled
      ? getCurrentHighlightedWord(currentTime)
      : null;

    return (
      <span>
        {segment.words.map((word, index) => {
          const isHighlighted = highlightedWord && word.start === highlightedWord.start;

          const wordStyle: React.CSSProperties = isHighlighted
            ? {
                backgroundColor: effectiveCaptionSettings.highlightColor,
                color: effectiveCaptionSettings.highlightWordColor,
              }
            : {};

          // Scale highlight box-shadow based on caption scale
          const scaledShadowSize = Math.round(BASE_HIGHLIGHT_SHADOW * captionScale);
          const scaledWordStyle: React.CSSProperties = isHighlighted
            ? {
                ...wordStyle,
                borderRadius: `${Math.round(3 * captionScale)}px`,
                boxShadow: `${scaledShadowSize}px 0 0 ${effectiveCaptionSettings.highlightColor}, -${scaledShadowSize}px 0 0 ${effectiveCaptionSettings.highlightColor}`,
              }
            : wordStyle;

          return (
            <span key={`${word.start}-${index}`}>
              <span style={scaledWordStyle}>{word.text}</span>
              {index < segment.words.length - 1 ? ' ' : ''}
            </span>
          );
        })}
      </span>
    );
  }, [
    currentTime,
    effectiveCaptionSettings,
    getCurrentSegment,
    getCurrentHighlightedWord,
    captionScale,
  ]);

  // Get scaled text shadow for border effect
  const getScaledTextShadow = useCallback(() => {
    // When adaptive contrast is enabled, always use 8px border with adaptive color
    if (effectiveCaptionSettings?.adaptiveContrast) {
      const baseWidth = 8 / 2; // 8px border width, divided by 2 for the shadow offset
      const scaledWidth = Math.max(1, Math.round(baseWidth * captionScale));
      const color = adaptiveBorderColor;

      const shadows = [];
      for (let x = -scaledWidth; x <= scaledWidth; x++) {
        for (let y = -scaledWidth; y <= scaledWidth; y++) {
          if (x !== 0 || y !== 0) {
            shadows.push(`${x}px ${y}px 0 ${color}`);
          }
        }
      }
      return shadows.join(', ');
    }

    if (!effectiveCaptionSettings?.borderEnabled) return 'none';

    // Scale the border width based on caption scale
    const baseWidth = parseInt(effectiveCaptionSettings.borderWidth, 10) / 2;
    const scaledWidth = Math.max(1, Math.round(baseWidth * captionScale));
    const color = effectiveCaptionSettings.borderColor;

    // Create multiple shadows for a solid outline effect
    const shadows = [];
    for (let x = -scaledWidth; x <= scaledWidth; x++) {
      for (let y = -scaledWidth; y <= scaledWidth; y++) {
        if (x !== 0 || y !== 0) {
          shadows.push(`${x}px ${y}px 0 ${color}`);
        }
      }
    }
    return shadows.join(', ');
  }, [effectiveCaptionSettings, captionScale, adaptiveBorderColor]);

  // Caption container style with scaled dimensions
  const captionStyle: React.CSSProperties = useMemo(() => {
    if (!effectiveCaptionSettings) return {};
    return {
      fontFamily: getCaptionFontFamily(effectiveCaptionSettings.captionFont as FontOption),
      fontWeight: getCaptionFontWeight(effectiveCaptionSettings.captionFont as FontOption),
      color: effectiveCaptionSettings.captionColor,
      textShadow: getScaledTextShadow(),
      // Scale font size and padding based on video container width
      fontSize: `${Math.round(BASE_CAPTION_FONT_SIZE * captionScale)}px`,
      padding: `${Math.round(BASE_CAPTION_PADDING_V * captionScale)}px ${Math.round(
        BASE_CAPTION_PADDING_H * captionScale,
      )}px`,
      borderRadius: `${Math.round(BASE_BORDER_RADIUS * captionScale)}px`,
      lineHeight: 1.4,
    };
  }, [effectiveCaptionSettings, getScaledTextShadow, captionScale]);

  // Caption overlay style with scaled bottom offset
  const captionOverlayStyle: React.CSSProperties = useMemo(
    () => ({
      bottom: `${Math.round(BASE_CAPTION_BOTTOM * captionScale)}px`,
      padding: `0 ${Math.round(20 * captionScale)}px`,
    }),
    [captionScale],
  );

  // const img = imageAtr?.image ? URL.createObjectURL(imageAtr.image) : '';

  // const sizeObj = useMemo(() => {
  //   switch (videoSize) {
  //     case '1:1':
  //       return {
  //         marginHorizontal: '0 25% 5% 25%',
  //       };
  //     case '16:9':
  //       return {
  //         marginHorizontal: '0',
  //       };
  //     case '9:16':
  //       return {
  //         marginHorizontal: '0 30% 3% 30%',
  //       };
  //     default:
  //       return {
  //         marginHorizontal: '0',
  //       };
  //   }
  // }, [videoSize]);

  return (
    <div className={styles.videoPlayerContainer} ref={containerRef}>
      <div style={headingContainerStyle}>
        <div style={headingStyle}>{headingTitle}</div>
      </div>
      {/* {addLogo && (
        <>
          {(imageAtr && imageAtr.image && imageAtr.image.size !== 0) || logo ? (
            <img
              src={logo ?? img}
              alt="image"
              style={{ ...imageAtr?.position, imageRendering: 'pixelated' }}
            />
          ) : (
            <></>
          )}
        </>
      )} */}
      <video
        controls={videoControls}
        playsInline
        ref={videoRef}
        className={styles.videoElement}
        crossOrigin="anonymous"
        style={
          {
            // height: 255,
            // padding: sizeObj.marginHorizontal,
            // objectFit: 'cover',
          }
        }
      >
        <source src={videoUrl} type="video/mp4" />
      </video>
      {/* Hidden canvas for video frame analysis */}
      <canvas ref={canvasRef} style={{ display: 'none' }} aria-hidden="true" />
      {/* Caption Overlay - sizes scale with video container */}
      {addCaptions && transcriptData && (
        <div className={styles.captionOverlay} style={captionOverlayStyle}>
          <div className={styles.captionText} style={captionStyle}>
            {renderCaptionText()}
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
